import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box, makeStyles } from '@material-ui/core';

import { Close } from '~/views/private/overview/ContentEditor/icons/Close';

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: '144px 80px',
    background: 'var(--surface-tint-strong, #171C2633)',
  },
  container: {
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    minWidth: 480,
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-default, #FFFFFF)',
    boxShadow: 'var(--shadows-modal)',
    overflow: 'hidden',
  },
  header: {
    boxSizing: 'border-box',
    flexShrink: 0,
    height: 'var(--utilities-size-3-500, 56px)',
    padding: '0 var(--utilities-size-0-750, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--utilities-size-0-500, 8px)',
  },
  title: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-lg)',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-lg-line)',
  },
  closeButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--content-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    boxSizing: 'border-box',
    position: 'relative',
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  media: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    userSelect: 'none',
  },
}));

function previewFileNameFromUrl(url) {
  const path = String(url || '').split('?')[0];
  return path.split('/').pop() || 'Content';
}

export const PreviewMediaModal = ({
  name,
  url,
  showVideo,
  reuseVideo,
  onVideoHost,
  onClose,
}) => {
  const classes = useStyles();
  const displayName = (name && name.trim()) || previewFileNameFromUrl(url);
  const useExistingVideo = Boolean(showVideo && reuseVideo && onVideoHost);

  const bodyRef = useCallback(
    node => {
      if (!useExistingVideo) return;
      onVideoHost(node);
    },
    [onVideoHost, useExistingVideo],
  );

  useEffect(() => {
    return () => {
      if (useExistingVideo) onVideoHost(null);
    };
  }, [onVideoHost, useExistingVideo]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <Box
      className={classes.overlay}
      onClick={onClose}
      onMouseDown={event => event.stopPropagation()}
    >
      <Box
        className={classes.container}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview ${displayName}`}
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
      >
        <Box className={classes.header}>
          <Box className={classes.title}>{displayName}</Box>
          <button
            type="button"
            className={classes.closeButton}
            onClick={onClose}
            aria-label="Close preview"
          >
            <Close color="var(--content-primary)" />
          </button>
        </Box>
        <Box
          ref={useExistingVideo ? bodyRef : undefined}
          className={classes.body}
        >
          {useExistingVideo ? null : showVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video src={url} className={classes.media} controls playsInline />
          ) : (
            <img
              src={url}
              alt={displayName}
              className={classes.media}
              draggable={false}
            />
          )}
        </Box>
      </Box>
    </Box>,
    document.body,
  );
};

PreviewMediaModal.propTypes = {
  name: PropTypes.string,
  url: PropTypes.string.isRequired,
  showVideo: PropTypes.bool,
  reuseVideo: PropTypes.bool,
  onVideoHost: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

PreviewMediaModal.defaultProps = {
  name: '',
  showVideo: false,
  reuseVideo: false,
  onVideoHost: undefined,
};
